import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AccessDeniedNotice } from '../../../shared/AccessDeniedNotice';
import { RetryableError } from '../../../shared/RetryableError';
import { ExportErrorCode, exportErrorMessage, hasCode, isForbidden } from '../exportErrors';
import { formatDateTime } from '../exportFormat';
import {
  DIMENSION_HELP, DIMENSION_LABEL, NET_PAY_COPY, NET_PAY_SHORT_LABEL, WAGE_MODE_COPY, WAGE_MODE_LABEL,
} from '../exportOptions';
import {
  NET_PAY_DETAIL_MODES, SELECTABLE_WAGE_MODES, WAGE_GROUP_DIMENSIONS, type DefaultAccountRef, type ExportSettings, type NetPayDetailMode, type WageGroupDimension, type WageMappingMode,
} from '../exportTypes';
import { accountingGateway } from '../index';
import { useExportCapabilities } from '../useExportCapabilities';
import '../../job-roles/job-roles.css';
import '../accounting-export.css';

type LoadState = { kind: 'loading' } | { kind: 'ready'; settings: ExportSettings } | { kind: 'forbidden' } | { kind: 'error'; message: string };
type SelectableWageMode = Exclude<WageMappingMode, 'LegacyResolver'>;

interface FormValues {
  netPay: NetPayDetailMode | null;
  wageMode: SelectableWageMode | null;
  dimension: WageGroupDimension | null;
}

/** The form starts from what is saved; with nothing saved (the original rules) nothing is preselected, so no choice is made by accident. */
const initialValues = (s: ExportSettings): FormValues => (s.isConfigured
  ? { netPay: s.netPayDetailMode, wageMode: s.wageMappingMode === 'LegacyResolver' ? null : s.wageMappingMode, dimension: s.wageGroupDimension }
  : { netPay: null, wageMode: null, dimension: null });

function DefaultAccount({ label, account, category, testId }: { label: string; account: DefaultAccountRef | null; category: string; testId: string }) {
  return (
    <div className="jr-facts__row" data-testid={testId}>
      <dt>{label}</dt>
      <dd>
        {account
          ? <><strong>{account.accountNumber}</strong> {account.accountName} </>
          : <span className="ax-status--missing">None mapped </span>}
        <Link to={account ? '/accounting?tab=gl-mapping' : `/accounting?tab=gl-mapping&add=1&category=${category}`} aria-label={`${account ? 'Review' : 'Add'} the ${label} in GL Mapping`}>
          {account ? 'Review in GL Mapping' : 'Add in GL Mapping'}
        </Link>
      </dd>
    </div>
  );
}

/**
 * Company export settings: the defaults every payroll export starts from (a person may still change them for one preview). Saving needs permission to manage GL
 * mappings; everyone else sees the saved values read-only. A save carries the version it was based on, so two people changing it at once never overwrite each other.
 * The default wage and clearing accounts are NOT stored here: they are the company-default mappings, shown with a shortcut to GL Mapping (one source of truth).
 */
export function ExportSettingsTab() {
  const uid = useId();
  const { caps } = useExportCapabilities();
  const canManage = caps?.canManageMappings !== false;
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [reloadNonce, setReloadNonce] = useState(0);
  const [values, setValues] = useState<FormValues>({ netPay: null, wageMode: null, dimension: null });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; conflict: boolean; needsDefault: boolean } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const noticeRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const controller = new AbortController();
    accountingGateway.getExportSettings(controller.signal)
      .then((settings) => {
        if (controller.signal.aborted) return;
        setState({ kind: 'ready', settings });
        setValues(initialValues(settings));
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setState(isForbidden(err) ? { kind: 'forbidden' } : { kind: 'error', message: exportErrorMessage(err, 'Could not load the export settings.') });
      });
    return () => controller.abort();
  }, [reloadNonce]);

  if ((caps && !caps.canPreview) || state.kind === 'forbidden') {
    return (
      <section className="ax-tab" aria-labelledby="ax-set-title">
        <h3 id="ax-set-title">Export settings</h3>
        <AccessDeniedNotice requiredPermissionLabel="permission to view GL mappings" />
      </section>
    );
  }

  const settings = state.kind === 'ready' ? state.settings : null;
  const saved = settings ? initialValues(settings) : null;
  const dirty = saved !== null && (values.netPay !== saved.netPay || values.wageMode !== saved.wageMode || (values.wageMode === 'GroupBased' && values.dimension !== saved.dimension));
  const complete = values.netPay !== null && values.wageMode !== null && (values.wageMode !== 'GroupBased' || values.dimension !== null);

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!settings || !complete || values.netPay === null || values.wageMode === null) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const next = await accountingGateway.saveExportSettings({
        netPayDetailMode: values.netPay, wageMappingMode: values.wageMode, ...(values.wageMode === 'GroupBased' && values.dimension ? { wageGroupDimension: values.dimension } : {}),
        expectedVersion: settings.settingsVersion,
      });
      setState({ kind: 'ready', settings: next });
      setValues(initialValues(next));
      setNotice(`Export settings saved (version ${next.settingsVersion}). Exports already generated are unchanged: each one keeps the options it was made with.`);
      queueMicrotask(() => noticeRef.current?.focus());
    } catch (err) {
      setError({
        message: exportErrorMessage(err, 'The export settings could not be saved.'),
        conflict: hasCode(err, ExportErrorCode.VersionConflict),
        needsDefault: hasCode(err, ExportErrorCode.DefaultWageAccountRequired),
      });
    } finally {
      setBusy(false);
    }
  }

  const readOnly = !canManage;
  const idOf = (k: string) => `${uid}-${k}`;

  return (
    <section className="ax-tab" aria-labelledby="ax-set-title">
      <h3 id="ax-set-title">Export settings</h3>
      <p className="muted ax-lead">
        The defaults every payroll export starts from. When you preview or export a payroll you can still change an option for that one export. Changing a setting never alters an export
        that was already generated: each export records the options it used.
      </p>

      {state.kind === 'loading' && <p role="status" className="muted">Loading export settings…</p>}
      {state.kind === 'error' && <RetryableError message={state.message} onRetry={() => { setState({ kind: 'loading' }); setReloadNonce((n) => n + 1); }} />}
      {notice && <p ref={noticeRef} tabIndex={-1} className="jr-notice jr-notice--ok" role="status" data-testid="settings-saved">{notice}</p>}

      {settings && (
        <>
          {readOnly && <p className="jr-readonly" role="note" data-testid="settings-readonly">You can view the export settings, but changing them needs permission to manage GL mappings, which you do not have.</p>}
          <p className="jr-hint" data-testid="settings-state">
            {settings.isConfigured
              ? <>Saved {settings.updatedAtUtc ? `on ${formatDateTime(settings.updatedAtUtc)}` : ''} (version {settings.settingsVersion}). Every change is recorded in the audit log with the old and new values.</>
              : <>No company setting is saved yet, so the original rules apply: one summarized net pay line for each mapped account and the original wage mapping order. Save a setting to make a choice the default.</>}
          </p>

          {error && (
            <div className="jr-error jr-error--block" role="alert" data-testid="settings-error">
              <p>{error.message}</p>
              {error.conflict && (
                <p>
                  Your changes were not saved.{' '}
                  <button type="button" className="ax-link-btn" onClick={() => { setError(null); setReloadNonce((n) => n + 1); }}>Load the latest settings</button>, then make your change again.
                </p>
              )}
              {error.needsDefault && <p><Link to="/accounting?tab=gl-mapping&add=1&category=EarningsGross">Add the default wage mapping in GL Mapping</Link></p>}
            </div>
          )}

          <form className="ax-settings-form" onSubmit={(e) => void save(e)} noValidate aria-label="Export settings">
            <fieldset className="jr-choice ax-setting-card" disabled={readOnly || busy} data-testid="settings-netpay">
              <legend>Net pay detail mode</legend>
              {NET_PAY_DETAIL_MODES.map((value) => (
                <div key={value} className="ax-opt">
                  <label>
                    <input type="radio" name={idOf('netpay')} value={value} checked={values.netPay === value} data-testid={`settings-netpay-${value}`} aria-describedby={idOf(`netpay-${value}`)}
                      onChange={() => setValues((v) => ({ ...v, netPay: value }))} />
                    <span>{NET_PAY_SHORT_LABEL[value]}</span>
                  </label>
                  <p id={idOf(`netpay-${value}`)} className="jr-hint ax-opt__copy">{NET_PAY_COPY[value]}</p>
                </div>
              ))}
              <p className="jr-hint">Applies when the export is a compound entry. In the per-employee mode every line is kept per employee already.</p>
            </fieldset>

            <fieldset className="jr-choice ax-setting-card" disabled={readOnly || busy} data-testid="settings-wage">
              <legend>Wage mapping mode</legend>
              {SELECTABLE_WAGE_MODES.map((value) => (
                <div key={value} className="ax-opt">
                  <label>
                    <input type="radio" name={idOf('wage')} value={value} checked={values.wageMode === value} data-testid={`settings-wage-${value}`} aria-describedby={idOf(`wage-${value}`)}
                      onChange={() => setValues((v) => ({ ...v, wageMode: value, dimension: value === 'GroupBased' ? (v.dimension ?? 'Department') : v.dimension }))} />
                    <span>{WAGE_MODE_LABEL[value]}</span>
                  </label>
                  <p id={idOf(`wage-${value}`)} className="jr-hint ax-opt__copy">{WAGE_MODE_COPY[value]}</p>
                </div>
              ))}
              <div className="jr-field ax-opt__dimension">
                <label htmlFor={idOf('dimension')}>Group dimension</label>
                <select id={idOf('dimension')} value={values.dimension ?? ''} disabled={values.wageMode !== 'GroupBased'} data-testid="settings-dimension" aria-describedby={idOf('dimension-help')}
                  onChange={(e) => setValues((v) => ({ ...v, dimension: (e.target.value || null) as WageGroupDimension | null }))}>
                  <option value="">{values.wageMode === 'GroupBased' ? 'Choose a dimension' : 'Only for By group'}</option>
                  {WAGE_GROUP_DIMENSIONS.map((d) => <option key={d} value={d}>{DIMENSION_LABEL[d]}</option>)}
                </select>
                <p id={idOf('dimension-help')} className="jr-hint">{values.wageMode === 'GroupBased' && values.dimension ? DIMENSION_HELP[values.dimension] : 'Required when wages are mapped by group.'}</p>
              </div>
              {values.wageMode === 'GroupBased' && values.dimension === 'EmployeeGroup' && (
                <p className="jr-hint"><Link to="/accounting?tab=gl-mapping&view=employee-groups">Manage employee groups</Link> in GL Mapping.</p>
              )}
              {values.wageMode === 'GroupBased' && values.dimension && (
                <p className="jr-hint"><Link to={`/accounting?tab=gl-mapping&view=by-group&dimension=${values.dimension}`}>Assign wage accounts to groups</Link> in GL Mapping.</p>
              )}
            </fieldset>

            <div className="ax-setting-card" data-testid="settings-accounts">
              <h4>Default accounts (read-only)</h4>
              <p className="jr-hint">These are the company-default mappings in GL Mapping, shown here so you can see what Single account and the clearing line use. Change them there.</p>
              <dl className="jr-facts jr-facts--tight">
                <DefaultAccount label="Default wage expense account" account={settings.defaultWageAccount} category="EarningsGross" testId="default-wage-account" />
                <DefaultAccount label="Default payroll clearing account" account={settings.defaultClearingAccount} category="NetPayClearing" testId="default-clearing-account" />
              </dl>
            </div>

            {!readOnly && (
              <div className="jr-actions">
                <button type="submit" className="jr-btn jr-btn--primary" disabled={!dirty || !complete || busy} data-testid="settings-save" aria-describedby={idOf('save-hint')}>
                  {busy ? 'Saving…' : 'Save settings'}
                </button>
                <button type="button" className="jr-btn jr-btn--secondary" disabled={!dirty || busy} onClick={() => { if (settings) setValues(initialValues(settings)); setError(null); }}>Discard changes</button>
              </div>
            )}
            {!readOnly && (
              <p id={idOf('save-hint')} className="jr-hint" data-testid="settings-save-hint">
                {!dirty ? 'Nothing has changed.' : !complete ? 'Choose a net pay detail mode and a wage mapping mode (and a group dimension for By group) to save.' : 'Saving records who changed what, and the old and new values, in the audit log.'}
              </p>
            )}
          </form>
        </>
      )}
    </section>
  );
}
